<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import AppSidebar from './AppSidebar.vue'
import AppSidebarTrigger from './AppSidebarTrigger.vue'
import AppChatDockButton from '../../chat/AppChatDockButton/AppChatDockButton.vue'
import AppBeam from '../../primitives/AppBeam/AppBeam.vue'
import AppResizeHandle from '../../primitives/AppResizeHandle/AppResizeHandle.vue'
import RaldoMark from '../../primitives/RaldoMark/RaldoMark.vue'
import { provideSidebar, type SidebarState } from './useSidebar'
import { provideChatDock } from '../../chat/AppChatPanel/useChatDock'
import { SHELL_SHEET } from './shell-surface'
import type { NavBadge, NavBrand, NavUser } from './nav-types'
import type { OnboardingChainStepView } from '@/types/onboarding-chain'
import { measureCssLengths, useEdgeResize } from '@/composables/useEdgeResize'
import { useArrivalSequence } from '@/composables/useArrivalSequence'
import { useHandoverSequence } from '@/composables/useHandoverSequence'
import { handoverSchedule } from '@/lib/handover/handover-schedule'

const props = withDefaults(
  defineProps<{
    /** The nav state when the user has not chosen one yet. */
    defaultState?: SidebarState
    defaultDockOpen?: boolean
    /** Shows the floating control that opens the dock. Off where the chat already fills the page. */
    dockButton?: boolean
    /** Signed-in user shown in the brand menu at the top of the side nav. */
    user?: NavUser | null
    /** Brand shown at the top of the side nav. Omit until it loads. */
    brand?: NavBrand | null
    /** Counts keyed by nav item id, shown as a badge on the matching item. */
    navBadges?: Record<string, NavBadge>
    /** Setup mode: the sidebar lists the chain's steps in place of the nav. */
    setup?: boolean
    /** The steps to list in setup mode, in chain order. */
    setupSteps?: readonly OnboardingChainStepView[]
    /** Plays the arrival once, on mount: the mark docks, the tile slides in and the setup steps unveil. Desktop only. */
    arrival?: boolean
    /** Plays the handover once, when it turns on: the nav rows, Home's content and the three floating controls arrive and a beam of light runs round the tile. Desktop only. */
    handover?: boolean
    /** The theme on screen now; the beam and the switch at the foot of the nav follow it. */
    theme?: 'light' | 'dark'
  }>(),
  {
    defaultState: 'expanded',
    defaultDockOpen: false,
    dockButton: true,
    user: null,
    brand: null,
    setup: false,
    setupSteps: () => [],
    arrival: false,
    handover: false,
    theme: 'light',
  },
)

const emit = defineEmits<{
  'sign-out': []
  'update:theme': [value: 'light' | 'dark']
  /** The arrival has finished; under reduced motion or on a phone it only uncovers the shell. */
  arrived: []
  /** The handover has finished; on a phone or under reduced motion it follows the change at once. */
  'handed-over': []
}>()

const { isMobile } = provideSidebar({
  defaultState: props.defaultState,
  forceExpanded: () => props.setup,
})

const dock = provideChatDock({ defaultOpen: props.defaultDockOpen })

const { t } = useI18n()

const dockSheetRef = ref<HTMLElement | null>(null)

function currentWidth(): number {
  return dockSheetRef.value?.getBoundingClientRect().width ?? dock.width.value
}

const bounds = ref({ min: 0, max: 0 })
function refreshBounds() {
  if (typeof document === 'undefined') {
    const width = currentWidth()
    bounds.value = { min: width, max: width }
    return
  }
  const [min = 0, max = 0] = measureCssLengths(['--dock-width-min', '--dock-width-max'])
  bounds.value = { min, max }
}

const handleValueNow = computed(() => Math.round(currentWidth()))
const handleValueMin = computed(() => Math.round(bounds.value.min))
const handleValueMax = computed(() => Math.round(bounds.value.max))

const dockResize = useEdgeResize({
  edge: 'left',
  getWidth: currentWidth,
  getBounds: () => bounds.value,
  setWidth: (px, persist) => dock.setWidth(px, persist),
  onActivate: () => dock.resetWidth(),
  onReset: () => dock.resetWidth(),
})
const resizing = dockResize.dragging
const dockHandlers = {
  onPointerdown: dockResize.onPointerdown,
  onPointermove: dockResize.onPointermove,
  onPointerup: dockResize.onPointerup,
  onPointercancel: dockResize.onPointercancel,
  onKeydown: dockResize.onKeydown,
  onDblclick: dockResize.onDblclick,
}

const gutterWidthClass = computed(() => (dock.isMobile.value ? 'w-0' : 'w-4'))

const dockTrackWidthClass = computed(() => {
  if (dock.isMobile.value) return 'w-0'
  return dock.open.value ? 'w-(--dock-track-width)' : 'w-0'
})

const dockTrackTransitionClass = computed(() =>
  resizing.value ? '' : 'transition-[width] duration-slow ease-soft',
)

const dockSheetClass = computed(() => {
  if (dock.isMobile.value) {
    return dock.open.value ? 'fixed inset-0 z-10 flex flex-col bg-bg-content' : 'hidden'
  }
  return [
    'my-4 mr-4 flex w-(--dock-width-effective) shrink-0 flex-col overflow-hidden',
    SHELL_SHEET,
  ].join(' ')
})

const arrivalSequence = useArrivalSequence()
const shellRef = ref<HTMLElement | null>(null)
const arriving = ref(props.arrival)
const staged = props.arrival && !isMobile.value && !arrivalSequence.reduced.value

async function playArrival(shell: HTMLElement): Promise<void> {
  if (staged) {
    await arrivalSequence.arrive(
      shell,
      props.setupSteps.map((entry) => entry.state),
    )
  } else {
    await arrivalSequence.uncover(shell)
  }
  arriving.value = false
  emit('arrived')
}

onMounted(() => {
  if (arriving.value && shellRef.value) void playArrival(shellRef.value)
})

const handoverSequence = useHandoverSequence()
const beamRef = ref<InstanceType<typeof AppBeam> | null>(null)
const tileRef = ref<HTMLElement | null>(null)
const handingOver = ref(false)

async function playHandover(shell: HTMLElement, tile: HTMLElement): Promise<void> {
  handingOver.value = true
  await nextTick()
  const beam = beamRef.value
  if (beam) {
    const { play } = beam
    const schedule = handoverSchedule({ stepRows: 0, navRows: 0, contentParts: 0 })
    await Promise.all([
      handoverSequence.arrive(shell),
      play(tile, { beam: schedule.beam, edgeGlow: schedule.edgeGlow }),
    ])
  }
  handingOver.value = false
}

watch(
  () => props.handover,
  async (handover) => {
    if (!handover) return
    const choreographed = !isMobile.value && !handoverSequence.reduced.value
    if (choreographed && shellRef.value && tileRef.value) {
      await playHandover(shellRef.value, tileRef.value)
    }
    emit('handed-over')
  },
  { flush: 'post' },
)

onMounted(() => {
  refreshBounds()
  if (typeof window !== 'undefined') window.addEventListener('resize', refreshBounds)
})
onBeforeUnmount(() => {
  if (typeof window !== 'undefined') window.removeEventListener('resize', refreshBounds)
})
</script>

<template>
  <div
    ref="shellRef"
    class="relative isolate flex h-svh w-full overflow-hidden bg-bg-surface text-text-primary"
  >
    <AppSidebar
      :user="user"
      :brand="brand"
      :nav-badges="navBadges"
      :setup="setup"
      :setup-steps="setupSteps"
      :handover="handover"
      :theme="theme"
      @sign-out="emit('sign-out')"
      @update:theme="emit('update:theme', $event)"
    >
      <template #nav-footer><slot name="nav-footer" /></template>
    </AppSidebar>
    <!-- HAZARD: the root is `h-svh` and the sheet inside main clips; a page scrolls only in its own AppScrollArea, never the sheet or the document. -->
    <main
      ref="tileRef"
      data-arrival="tile"
      :class="[
        'relative my-4 flex w-full min-w-0 flex-1 flex-col overflow-hidden',
        SHELL_SHEET,
        $slots.dock ? '' : 'mr-4',
      ]"
    >
      <div
        class="relative flex min-h-0 flex-1 flex-col overflow-hidden"
        :class="$slots.banner ? 'shell-has-banner' : undefined"
      >
        <div v-if="!setup && isMobile" class="icon-sm absolute left-4 top-4 z-30 flex">
          <AppSidebarTrigger />
        </div>
        <div
          v-if="$slots.banner"
          class="shrink-0 pr-4 pt-4"
          :class="isMobile && !setup ? 'pl-24' : 'pl-4'"
        >
          <slot name="banner" />
        </div>
        <slot />
      </div>
    </main>

    <template v-if="$slots.dock">
      <div class="relative shrink-0" :class="gutterWidthClass">
        <AppResizeHandle
          v-if="dock.open.value && !dock.isMobile.value"
          :label="t('chat.dock.resize')"
          :value-now="handleValueNow"
          :value-min="handleValueMin"
          :value-max="handleValueMax"
          :dragging="resizing"
          class="z-20"
          v-bind="dockHandlers"
        />
      </div>
      <div
        class="flex shrink-0 overflow-hidden"
        :class="[dockTrackWidthClass, dockTrackTransitionClass]"
        :inert="!dock.open.value"
      >
        <div ref="dockSheetRef" :class="dockSheetClass">
          <slot name="dock" />
        </div>
      </div>
    </template>

    <div
      v-if="arriving"
      class="pointer-events-none fixed inset-0 z-30 grid place-items-center"
      aria-hidden="true"
    >
      <div data-arrival="ground" class="absolute inset-0 bg-bg-content opacity-0" />
      <div v-if="staged" data-arrival="flight" class="relative flex opacity-0">
        <div data-arrival="hero" class="flex">
          <RaldoMark size="xl" decorative />
        </div>
      </div>
    </div>

    <div v-if="handingOver" class="pointer-events-none fixed inset-0 z-30" aria-hidden="true">
      <AppBeam ref="beamRef" :scheme="theme" />
    </div>

    <div class="fixed bottom-24 right-24 z-20 flex flex-col items-end gap-8">
      <slot name="floating" />
      <div v-if="$slots.dock && dockButton && !dock.open.value" data-arrival="dock-button">
        <AppChatDockButton />
      </div>
    </div>
  </div>
</template>
